import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { withSilentForbidden } from 'src/app/core/interceptors/error.interceptor';
import { PartnerMemberRole } from './partner-users.service';

/** Lo que dice el backend de una invitación al equipo de un partner. */
export interface PartnerInvitation {
  token: string;
  email: string;
  role: PartnerMemberRole;
  partner: { name: string; slug: string; logoUrl: string | null };
  /** `false` → registro con el token; `true` → login y aceptar. */
  hasAccount: boolean;
  expiresAt: string | null;
}

const STORAGE_KEY = 'partnerInvitation';

/**
 * Invitaciones al equipo de un partner (WS-2259). El correo lleva
 * `/accept-invitation?partner={slug}&token={token}`:
 *
 * - `GET  partner-invitations/{token}` (público, con App Check; 20/min) →
 *   `{ email, role, partner: { name, slug, logo_url }, has_account, expires_at }`;
 *   404 si no vale (inexistente, caducada, cancelada o usada).
 * - `POST partner-invitations/{token}/accept` (con sesión) → asigna el rol.
 *   403 si la sesión es de otro email; 404 si ya no vale; 422 si el usuario ya
 *   es de un partner, administra empresas (subtutor) o es una cuenta borrada.
 * - `register` y `social-register` aceptan `invitation_token`: con él, el alta
 *   recibe el rol invitado y la invitación queda aceptada.
 *
 * La invitación en curso se guarda en `sessionStorage` mientras dura el alta o
 * el login, para mandar el token al registrarse y para volver a
 * `/accept-invitation` a aceptarla después de iniciar sesión.
 */
@Injectable({ providedIn: 'root' })
export class PartnerInvitationService {
  private readonly API = `${environment.laravelBackoffice}/partner-invitations`;

  constructor(private http: HttpClient) {}

  resolve(token: string): Observable<PartnerInvitation> {
    return this.http.get<any>(`${this.API}/${encodeURIComponent(token)}`).pipe(
      map((res) => {
        const d = res?.data ?? res;
        return {
          token,
          email: d?.email ?? '',
          role: d?.role === 'corporation_tutor' ? 'corporation_tutor' : 'corporation_subtutor',
          partner: {
            name: d?.partner?.name ?? '',
            slug: d?.partner?.slug ?? '',
            logoUrl: d?.partner?.logo_url ?? null,
          },
          hasAccount: !!d?.has_account,
          expiresAt: d?.expires_at ?? null,
        } as PartnerInvitation;
      }),
    );
  }

  /**
   * El 403 aquí es «esta invitación es para otro email», que la pantalla ya
   * explica: sin el aviso genérico de acceso denegado.
   */
  accept(token: string): Observable<unknown> {
    return this.http.post(
      `${this.API}/${encodeURIComponent(token)}/accept`,
      {},
      { context: withSilentForbidden() },
    );
  }

  /** Invitación en curso (alta o login a medias), o `null`. */
  get pending(): PartnerInvitation | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PartnerInvitation) : null;
    } catch {
      return null;
    }
  }

  setPending(invitation: PartnerInvitation): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(invitation));
    } catch {
      // Sin almacenamiento (modo privado estricto): el alta sigue sin el token.
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nada que limpiar */
    }
  }
}
